// Events
import { SEARCH_QUERY, SEARCH_RESULTS, SEARCH_SUBMIT, SEARCH_SUGGESTIONS } from 'events';
// Lib
import Search from 'lib/algolia/search';
import PhillRecommendations from 'lib/PhillRecommendations';
import Tracking from 'lib/tracking';
import { component } from 'picoapp';

// Utils
import choozy from 'utils/choozy';
import { hide, show } from 'utils/node';
import { searchListItem, searchProductItem, searchSuggestionItem } from 'utils/product-item';
import getString from 'utils/string';

export default component(async (node, ctx) => {
  const {
    input,
    searchResultsContainer,
    searchResultsProducts,
    searchResultsProductsList,
    searchResultsCollections,
    searchResultsCollectionsList,
    searchResultsPages,
    searchResultsPagesList,
    searchResultsArticles,
    searchResultsArticlesList,
    emptyInfo,
    allResultsLink,
    recommendationsProductsList,
    searchSuggestionsContainer,
    searchSuggestionsList,
    searchRecommendationsProducts,
    searchForm,
  } = choozy(node);

  const {
    indices: { productsIndex },
    algoliaQuerySuggestionsIndex,
    appId,
    apiKey,
  } = window.theme.algoliaSettings;

  let recommendationsInitialized = false;

  const currentResults = {
    products: null,
    collections: null,
    pages: null,
    articles: null,
  };

  // Utils
  const search = new Search(ctx);

  const recommendations = new PhillRecommendations({
    appId,
    apiKey,
    index: productsIndex,
    retrievableAttributes: [
      'title',
      'handle',
      'product_image',
      'product_type',
      'price',
      'compare_at_price',
    ],
  });

  const trackSearch = (query, count) => {
    window.dataLayer.push({
      event: 'internalSearch',
      eventCategory: 'Internal Search',
      eventAction: 'Search',
      eventLabel: query,
      eventValue: count,
    });
  };

  searchResultsContainer.addEventListener('click', (event) => {
    const currentItem = event.target.closest('[data-search-item-link]');
    const type = currentItem?.dataset.searchItemLink;
    const index = currentItem?.dataset.searchItemIndex;

    if (!currentItem) return;

    window.aa('clickedObjectIDsAfterSearch', {
      eventName: `Search fly-out: Clicked ${type} result`,
      eventType: 'click',
      index: currentResults[type].index,
      queryID: currentResults[type].queryID,
      objectIDs: [currentResults[type].hits[index].objectID],
      positions: [+index + 1],
      currency: window.Shopify.currency.active,
    });
    localStorage.setItem(
      'algolia_recent_product',
      JSON.stringify({
        queryID: currentResults[type].queryID,
        id: currentResults[type].hits[index].id,
      }),
    );
  });

  const initRecommendations = async () => {
    if (recommendationsInitialized) return;

    try {
      const [hits] = await recommendations.query(['trending-items']);

      recommendationsProductsList.innerHTML = hits
        .map((hit) =>
          searchProductItem({
            ...hit,
            url: `/products/${hit.handle}`,
            image: {
              url: hit.product_image,
              width: 750,
              height: 1000,
            },
            type: hit.product_type,
            price: hit.price * 100,
            compare_at_price_max: hit.compare_at_price * 100,
          }),
        )
        .join('');

      Tracking.trackViewItemList({
        products: hits.map((item, index) => ({
          ...item,
          index,
          price: item.price * 100,
        })),
        placement: 'search_flyout_recommendations',
      });

      recommendationsInitialized = true;
    } catch (err) {
      // eslint-disable-next-line
      console.error(err);
    }
  };

  const handleSearch = (searchState) => {
    const { query, results } = searchState;

    if (query === '') return;

    if (allResultsLink) {
      allResultsLink.href = encodeURI(`/search?${productsIndex}[query]=${query}`);
    }

    show(searchResultsContainer);

    const resultsCounts = ['articles', 'collections', 'products', 'pages'].reduce(
      (totalNumberOfHits, resourceName) => {
        currentResults[resourceName] = results[resourceName];

        return totalNumberOfHits + (results[resourceName]?.nbHits || 0);
      },
      0,
    );

    if (resultsCounts === 0) {
      show(emptyInfo);
      emptyInfo.textContent = getString('search.no_results_header', { query });
    } else {
      hide(emptyInfo);
    }

    trackSearch(query, resultsCounts);

    if (results.products.hits.length === 0) {
      initRecommendations();
      show(searchRecommendationsProducts);
      hide(searchResultsProducts);
    } else {
      hide(searchRecommendationsProducts);
      show(searchResultsProducts);

      searchResultsProductsList.innerHTML = results.products.hits
        .map((hit, index) =>
          searchProductItem(
            {
              ...hit,
              url: `/products/${hit.handle}`,
              image: {
                url: hit.product_image,
                width: 750,
                height: 1000,
              },
              type: hit.product_type,
              price: hit.price * 100,
              compare_at_price_max: hit.compare_at_price * 100,
              index,
            },
            ['data-search-item-link="products"', `data-search-item-index="${index}"`],
          ),
        )
        .join('');

      Tracking.trackViewItemList({
        products: results.products.hits.map((item, index) => ({
          ...item,
          index,
          price: item.price * 100,
        })),
        placement: 'search_flyout',
      });
    }

    if (
      results.collections.hits.length + results.pages.hits.length + results.articles.hits.length ===
      0
    ) {
      show(searchSuggestionsContainer);
      hide(searchResultsCollections, searchResultsPages, searchResultsArticles);
    } else {
      hide(searchSuggestionsContainer);
      show(searchResultsCollections, searchResultsPages, searchResultsArticles);
    }

    searchResultsCollectionsList.innerHTML = results.collections.hits
      .map((hit, index) =>
        searchListItem({ ...hit, url: `/collections/${hit.handle}` }, [
          'data-search-item-link="collections"',
          `data-search-item-index="${index}"`,
        ]),
      )
      .join('');

    searchResultsPagesList.innerHTML = results.pages.hits
      .map((hit, index) =>
        searchListItem({ ...hit, url: `/pages/${hit.handle}` }, [
          'data-search-item-link="pages"',
          `data-search-item-index="${index}"`,
        ]),
      )
      .join('');

    searchResultsArticlesList.innerHTML = results.articles.hits
      .map((hit, index) =>
        searchListItem({ ...hit, url: `/blogs/${hit.blog.handle}/${hit.handle}` }, [
          'data-search-item-link="articles"',
          `data-search-item-index="${index}"`,
        ]),
      )
      .join('');
  };

  ctx.on(SEARCH_RESULTS, (state) => {
    handleSearch(state.search);
  });

  ctx.on(SEARCH_QUERY, ({ search: searchState }) => {
    const { query } = searchState;

    if (query === '') {
      node.classList.add('empty-state');
      show(searchSuggestionsContainer);
      hide(
        emptyInfo,
        searchRecommendationsProducts,
        searchResultsCollections,
        searchResultsPages,
        searchResultsArticles,
        searchResultsProducts,
      );
    } else {
      node.classList.remove('empty-state');
    }
  });

  ctx.on(SEARCH_SUGGESTIONS, (state, indices) => {
    const suggestionsIndex = indices.find(
      ({ indexName }) => indexName === algoliaQuerySuggestionsIndex,
    );

    if (suggestionsIndex && (suggestionsIndex.hits ?? []).length > 0) {
      searchSuggestionsList.innerHTML = suggestionsIndex.hits
        .map((hit) => searchSuggestionItem({ ...hit, url: `/collections/${hit.handle}` }))
        .join('');
    }
  });

  input.addEventListener('input', () => {
    const { search: searchState } = ctx.getState();
    ctx.emit(SEARCH_QUERY, { search: { ...searchState, query: input.value } }, 'header');
  });

  ctx.on(SEARCH_QUERY, ({ search: searchState }, emitterId) => {
    if (emitterId === 'header') return;

    if (search.query !== input.value) {
      input.value = searchState.query;
    }
  });

  const submit = (query) => {
    window.location = `/search?${productsIndex}[query]=${query}`;
  };

  ctx.on(SEARCH_SUBMIT, ({ search: searchState }) => {
    const { query } = searchState;

    submit(query);
  });

  searchSuggestionsContainer.addEventListener('click', (e) => {
    const button = e.target.closest('button');

    if (button) {
      const { search: searchState } = ctx.getState();
      ctx.emit(SEARCH_QUERY, { search: { ...searchState, query: button.textContent } });
    }
  });

  searchForm.addEventListener('submit', (e) => {
    e.preventDefault();

    const { search: searchState } = ctx.getState();
    const { query } = searchState;

    submit(query);
  });

  searchResultsProductsList.addEventListener('click', (event) =>
    Tracking.selectItemCallback(event, false, true),
  );

  search.start();
});
